// src/components/fights/FightCard.jsx
import React, { useState, useEffect, useCallback } from "react";
import useToast from "../../hooks/useToast";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ExclamationIcon,
  CheckCircleIcon,
  XIcon,
  RefreshIcon,
} from "@heroicons/react/solid";

import { useUpdateMoneyline } from "../../hooks/events/useUpdateMoneyline";
import { useUpdateFightResults } from "../../hooks/events/useUpdateFightResults";
import { useRemoveFightFromEvent } from "../../hooks/events/useRemoveFightFromEvent";
import { useRestoreFightToEvent } from "../../hooks/events/useRestoreFightToEvent";
import { useUpdateMainEvent } from "../../hooks/events/useUpdateMainEvent";
import { useUpdateWeightClass } from "../../hooks/events/useUpdateWeightClass";

// NEW admin hooks
import { useFetchUser } from "../../hooks/user/useFetchUser";
import { useUpdateLeagueMemberScores } from "../../hooks/league/useUpdateLeagueMemberScores";
import { useUpdateEventStatus } from "../../hooks/events/useUpdateEventStatus";

import FighterDetails from "./FighterDetails";
import RemoveFightConfirmationModal from "./RemoveFightConfirmationModal";
import useIsMobile from "../../hooks/mobile/useIsMobile";
import WeightClassSelector from "./WeightClassSelector";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { useEventDetails } from "../../hooks/events/useEventDetails";
import Loading from "../common/Loading";
import EventAdminHeader from "./EventAdminHeader";
import { useDispatch } from "react-redux";
import { setEventName } from "../../features/event/eventReducer";

/**
 * Renders ALL fights from eventDetails and (if admin) shows admin controls
 * Props:
 *   - expandAll?: boolean (expand/collapse all fight items)
 */
const FightCard = ({ expandAll = false }) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { eventId } = useParams();
  const toast = useToast();

  // Expand/collapse all state
  const [allExpanded, setAllExpanded] = useState(expandAll);

  const {
    data: eventDetails,
    isLoading: eventLoading,
    error: eventError,
  } = useEventDetails(eventId);

  // Admin context
  const { data: user } = useFetchUser();
  const isAdmin = user?.role === "admin";

  // Status + admin mutations
  const [status, setStatus] = useState(eventDetails?.Status || "in progress");
  const updateEventStatusMutation = useUpdateEventStatus();
  const updateLeagueMemberScoresMutation = useUpdateLeagueMemberScores();

  useEffect(() => {
    if (eventDetails?.Status) setStatus(eventDetails.Status);
  }, [eventDetails?.Status]);

  // Update Redux state with correct event name when eventDetails loads
  useEffect(() => {
    if (eventDetails?.Name) {
      dispatch(setEventName(eventDetails.Name));
    }
  }, [eventDetails?.Name, dispatch]);

  const handleMutationSuccess = () => {
    queryClient.invalidateQueries(["eventDetails", eventId]);
  };

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
    updateEventStatusMutation.mutate(
      { eventId, status: newStatus },
      {
        onSuccess: () => {
          toast.success(`Event status updated to ${newStatus}.`);
          handleMutationSuccess();
        },
        onError: (error) => {
          toast.error(`Failed to update event status: ${error.message}`);
        },
      },
    );
  };

  const handleUpdateLeagueMemberScores = () => {
    updateLeagueMemberScoresMutation.mutate(eventId, {
      onSuccess: () =>
        toast.success("League member scores updated successfully."),
      onError: (error) =>
        toast.error(`Failed to update league member scores: ${error.message}`),
    });
  };

  if (eventLoading) return <Loading />;
  if (eventError)
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800">
        Failed to load event details: {eventError.message}
      </div>
    );

  const fights = eventDetails?.Fights ?? [];

  // Separate active and cancelled fights
  const activeFights = fights.filter((fight) => !fight.hidden);
  const cancelledFights = fights.filter((fight) => fight.hidden);

  if (!fights.length) {
    return (
      <div className="bg-gray-50 rounded-xl p-8 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <ExclamationIcon
            className="w-8 h-8 text-gray-400"
            aria-hidden="true"
          />
        </div>
        <h3 className="text-lg font-bold text-dark-grey mb-2">
          No fights in this event
        </h3>
        <p className="text-medium-grey">
          Add fights to manage moneylines and results.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EventAdminHeader eventDetails={eventDetails} status={status} />

      {/* Expand/Collapse All Controls */}
      {activeFights.length > 1 && (
        <div className="flex justify-center">
          <div className="inline-flex items-center bg-white rounded-lg border border-gray-200 shadow-sm">
            <button
              onClick={() => setAllExpanded(true)}
              className={`px-4 py-2 text-sm font-medium rounded-l-lg transition-all duration-200 flex items-center gap-2 ${
                allExpanded
                  ? "bg-blue-50 text-blue-700 border-r border-blue-200 shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-r border-gray-200"
              }`}
              title="Expand all fights to see details"
            >
              <ChevronDownIcon className="w-4 h-4" />
              Expand All ({activeFights.length})
            </button>
            <button
              onClick={() => setAllExpanded(false)}
              className={`px-4 py-2 text-sm font-medium rounded-r-lg transition-all duration-200 flex items-center gap-2 ${
                !allExpanded
                  ? "bg-blue-50 text-blue-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
              title="Collapse all fights to hide details"
            >
              <ChevronUpIcon className="w-4 h-4" />
              Collapse All
            </button>
          </div>
        </div>
      )}

      {/* Active Fights list */}
      <div className="space-y-4">
        {activeFights.map((fight) => (
          <FightItem
            key={fight.FightId}
            fight={fight}
            eventId={eventId}
            expandAll={allExpanded}
          />
        ))}
      </div>

      {/* Cancelled Fights Section */}
      {cancelledFights.length > 0 && (
        <div className="space-y-4 mt-8">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-bold text-dark-grey">Cancelled</h3>
            <span className="text-sm text-medium-grey bg-gray-100 px-2.5 py-1 rounded-full font-medium">
              {cancelledFights.length}{" "}
              {cancelledFights.length === 1 ? "fight" : "fights"}
            </span>
          </div>
          <div className="space-y-4">
            {cancelledFights.map((fight) => (
              <FightItem
                key={fight.FightId}
                fight={fight}
                eventId={eventId}
                expandAll={allExpanded}
              />
            ))}
          </div>
        </div>
      )}

      {/* Admin controls (styled to match app) */}
      {isAdmin && (
        <div className="card max-w-4xl mx-auto p-6 space-y-5">
          {/* Status segmented control */}
          <div className="panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-medium-grey">
                  Event Status
                </span>
              </div>

              <div
                className="segmented"
                role="tablist"
                aria-label="Event status"
              >
                {/* READY */}
                <button
                  type="button"
                  role="tab"
                  aria-selected={status === "ready"}
                  onClick={() => handleStatusChange("ready")}
                  disabled={updateEventStatusMutation.isLoading}
                  className={`segmented__btn ${
                    status === "ready" ? "is-active is-success" : ""
                  } ${
                    updateEventStatusMutation.isLoading
                      ? "opacity-60 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {updateEventStatusMutation.isLoading && status !== "ready" ? (
                    <svg
                      className="w-4 h-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        className="opacity-25"
                      ></circle>
                      <path
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        className="opacity-75"
                      ></path>
                    </svg>
                  ) : (
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0L3.293 9.207a1 1 0 111.414-1.414l3.043 3.043 6.543-6.543a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  Ready
                </button>

                {/* IN PROGRESS */}
                <button
                  type="button"
                  role="tab"
                  aria-selected={status === "in progress"}
                  onClick={() => handleStatusChange("in progress")}
                  disabled={updateEventStatusMutation.isLoading}
                  className={`segmented__btn ${
                    status === "in progress" ? "is-active is-danger" : ""
                  } ${
                    updateEventStatusMutation.isLoading
                      ? "opacity-60 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {updateEventStatusMutation.isLoading &&
                  status !== "in progress" ? (
                    <svg
                      className="w-4 h-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        className="opacity-25"
                      ></circle>
                      <path
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        className="opacity-75"
                      ></path>
                    </svg>
                  ) : (
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M2 10a8 8 0 1113.657 5.657l.707.707A9 9 0 101 10h1z" />
                      <path d="M12 8a.75.75 0 00-1.5 0v3.25c0 .199.079.39.22.53l2 2a.75.75 0 101.06-1.06L12 10.44V8z" />
                    </svg>
                  )}
                  In Progress
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={handleUpdateLeagueMemberScores}
              disabled={updateLeagueMemberScoresMutation.isLoading}
              className={`btn btn--soft-primary btn--pill w-full justify-center ${
                updateLeagueMemberScoresMutation.isLoading
                  ? "opacity-60 cursor-not-allowed"
                  : ""
              }`}
            >
              {updateLeagueMemberScoresMutation.isLoading ? (
                <svg
                  className="w-4 h-4 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    className="opacity-25"
                  ></circle>
                  <path
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    className="opacity-75"
                  ></path>
                </svg>
              ) : (
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M4 4v6h6M20 20v-6h-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M20 8a8 8 0 00-15.5-2M4 16a8 8 0 0015.5 2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {updateLeagueMemberScoresMutation.isLoading
                ? "Updating Scores..."
                : "Update League Member Scores"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Private per-fight item — unchanged behavior; styled to match app
 */
const FightItem = ({ fight, eventId, expandAll }) => {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [outcomes, setOutcomes] = useState({});
  const [moneylines, setMoneylines] = useState(
    fight.Fighters.reduce(
      (acc, f) => ({ ...acc, [f.FighterId]: f.Moneyline }),
      {},
    ),
  );
  const [isMainEvent, setIsMainEvent] = useState(fight.Rounds === 5);
  const [weightClass, setWeightClass] = useState(fight.WeightClass);
  const [collapsed, setCollapsed] = useState(!expandAll);
  const [updatingMoneylineFor, setUpdatingMoneylineFor] = useState(null);

  useEffect(() => {
    setIsMainEvent(fight.Rounds === 5);
    setWeightClass(fight.WeightClass);
    setMoneylines(
      fight.Fighters.reduce(
        (acc, f) => ({ ...acc, [f.FighterId]: f.Moneyline }),
        {},
      ),
    );
  }, [fight]);

  useEffect(() => {
    setCollapsed(!expandAll);
  }, [expandAll]);

  const updateMoneylineMutation = useUpdateMoneyline();
  const updateFightResultsMutation = useUpdateFightResults();
  const removeFightMutation = useRemoveFightFromEvent();
  const restoreFightMutation = useRestoreFightToEvent();
  const updateMainEventMutation = useUpdateMainEvent();
  const updateWeightClassMutation = useUpdateWeightClass();

  const handleMutationSuccess = () => {
    queryClient.invalidateQueries(["eventDetails", eventId]);
  };

  const handleRemoveFight = () => {
    removeFightMutation.mutate(
      { eventId, fightId: fight.FightId },
      {
        onSuccess: () => {
          toast.success("Fight removed successfully from the event.");
          handleMutationSuccess();
        },
        onError: (error) => {
          toast.error(
            `Failed to remove fight from the event: ${error.message}`,
          );
        },
      },
    );
  };

  const handleRestoreFight = () => {
    restoreFightMutation.mutate(
      { eventId, fightId: fight.FightId },
      {
        onSuccess: () => {
          toast.success("Fight restored successfully to the event.");
          handleMutationSuccess();
        },
        onError: (error) => {
          toast.error(`Failed to restore fight to the event: ${error.message}`);
        },
      },
    );
  };

  const handleMoneylineChange = (fighterId, e) => {
    const value = e.target.value;
    setMoneylines((prev) => ({
      ...prev,
      [fighterId]: parseInt(value, 10) || 0,
    }));
  };

  const updateMoneyline = async (eventIdArg, fightIdArg, fighterId) => {
    setUpdatingMoneylineFor(fighterId);
    updateMoneylineMutation.mutate(
      {
        eventId: eventIdArg,
        fightId: fightIdArg,
        fighterId,
        moneyline: moneylines[fighterId],
      },
      {
        onSuccess: (data) => {
          toast.success("Moneyline updated successfully", data);
          handleMutationSuccess();
          setUpdatingMoneylineFor(null);
        },
        onError: (error) => {
          toast.error("Failed to update moneyline", error);
          setUpdatingMoneylineFor(null);
        },
      },
    );
  };

  const handleUpdateOutcome = useCallback((fighterOutcome) => {
    setOutcomes((prev) => ({
      ...prev,
      [fighterOutcome.fighterId]: fighterOutcome,
    }));
  }, []);

  const handleSubmitAllOutcomes = () => {
    updateFightResultsMutation.mutate(
      { eventId, fightId: fight.FightId, fighterResults: outcomes },
      {
        onSuccess: () => {
          toast.success("Fight outcomes updated successfully!");
          handleMutationSuccess();
        },
        onError: (error) => {
          toast.error(`Failed to update fight outcomes: ${error.message}`);
        },
      },
    );
  };

  const handleMainEventChange = (e) => {
    const isChecked = e.target.checked;
    setIsMainEvent(isChecked);

    updateMainEventMutation.mutate(
      { eventId, fightId: fight.FightId, isMainEvent: isChecked },
      {
        onSuccess: () => {
          toast.success(
            `Main event status updated. Rounds set to ${isChecked ? 5 : 3}.`,
          );
          handleMutationSuccess();
        },
        onError: (error) => {
          toast.error(`Failed to update main event status: ${error.message}`);
        },
      },
    );
  };

  const handleWeightClassChange = (e) => {
    const newWeightClass = e.target.value;
    setWeightClass(newWeightClass);

    updateWeightClassMutation.mutate(
      { eventId, fightId: fight.FightId, weightClass: newWeightClass },
      {
        onSuccess: () => {
          toast.success(`Weight class updated to ${newWeightClass}.`);
          handleMutationSuccess();
        },
        onError: (error) => {
          toast.error(`Failed to update weight class: ${error.message}`);
        },
      },
    );
  };

  const isMoneylineMissing = fight.Fighters.some(
    (f) => moneylines[f.FighterId] == null,
  );
  const isResultsSubmitted = fight.resultsSubmitted;

  return (
    <div
      className={[
        "card relative my-4",
        isMobile ? "p-4" : "p-6",
        isMoneylineMissing ? "ring-1 ring-yellow-200" : "",
        isResultsSubmitted ? "ring-1 ring-green-200" : "",
      ].join(" ")}
    >
      {/* Status indicators */}
      <div className="absolute top-3 right-3 flex items-center gap-2">
        {fight.hidden && (
          <span className="chip chip--muted inline-flex items-center gap-1 bg-gray-600 text-white">
            <svg
              className="w-4 h-4"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z"
                clipRule="evenodd"
              />
              <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
            </svg>
            Hidden
          </span>
        )}
        {isMoneylineMissing && (
          <span className="chip chip--warn inline-flex items-center gap-1">
            <ExclamationIcon className="w-4 h-4" aria-hidden="true" />
          </span>
        )}
        {isResultsSubmitted && (
          <span className="chip chip--ok inline-flex items-center gap-1">
            <CheckCircleIcon className="w-4 h-4" aria-hidden="true" />
          </span>
        )}
      </div>

      {/* Header (name left, discussion icon and chevron right) */}
      <div className="flex items-center justify-between gap-3">
        <h2 className="min-w-0 text-lg md:text-xl font-bold text-dark-grey leading-tight">
          <span className="block truncate sm:whitespace-normal">
            {fight.Fighters.map((f) => `${f.FirstName} ${f.LastName}`).join(
              " vs. ",
            )}
          </span>
        </h2>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Expand/Collapse Button */}
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="btn btn--ghost px-3 py-2"
            aria-expanded={!collapsed}
            aria-controls={`fight-panel-${fight.FightId}`}
            aria-label={collapsed ? "Expand fight" : "Collapse fight"}
          >
            {collapsed ? (
              <ChevronDownIcon className="w-4 h-4" aria-hidden="true" />
            ) : (
              <ChevronUpIcon className="w-4 h-4" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {/* Collapsible Content */}
      <div
        id={`fight-panel-${fight.FightId}`}
        role="region"
        className={[
          "overflow-hidden transition-all duration-300",
          collapsed
            ? "max-h-0 opacity-0 pointer-events-none"
            : "max-h-[2000px] opacity-100",
        ].join(" ")}
      >
        <div className="mt-4 space-y-4">
          {/* Controls with remove button */}
          <div className="flex items-start justify-between gap-4">
            <div
              className={[
                "flex",
                isMobile
                  ? "flex-col items-start gap-3"
                  : "flex-col items-start gap-3",
              ].join(" ")}
            >
              <label
                htmlFor={`main-event-${fight.FightId}`}
                className={`inline-flex items-center gap-2 text-dark-grey font-semibold ${
                  updateMainEventMutation.isLoading ? "opacity-60" : ""
                }`}
              >
                <input
                  type="checkbox"
                  id={`main-event-${fight.FightId}`}
                  className="rounded border-gray-300 text-deep-red focus:ring-deep-red/40"
                  checked={isMainEvent}
                  onChange={handleMainEventChange}
                  disabled={updateMainEventMutation.isLoading}
                />
                {updateMainEventMutation.isLoading ? (
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        className="opacity-25"
                      ></circle>
                      <path
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        className="opacity-75"
                      ></path>
                    </svg>
                    Updating...
                  </div>
                ) : (
                  "Main Event"
                )}
              </label>

              <WeightClassSelector
                weightClass={weightClass}
                onChange={handleWeightClassChange}
                fightId={fight.FightId}
                isLoading={updateWeightClassMutation.isLoading}
              />
            </div>

            {/* Remove/Restore fight button - only visible when expanded */}
            {fight.hidden ? (
              <button
                onClick={handleRestoreFight}
                disabled={restoreFightMutation.isLoading}
                className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 hover:text-blue-700 transition-colors duration-200 flex-shrink-0"
                title="Restore fight to event"
                aria-label="Restore fight to event"
              >
                {restoreFightMutation.isLoading ? (
                  <svg
                    className="w-4 h-4 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      className="opacity-25"
                    ></circle>
                    <path
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      className="opacity-75"
                    ></path>
                  </svg>
                ) : (
                  <RefreshIcon className="w-4 h-4" aria-hidden="true" />
                )}
              </button>
            ) : (
              <button
                onClick={() => setShowConfirmModal(true)}
                disabled={removeFightMutation.isLoading}
                className="p-2 rounded-full bg-red-100 hover:bg-red-200 text-red-600 hover:text-red-700 transition-colors duration-200 flex-shrink-0"
                title="Remove fight from event"
                aria-label="Remove fight from event"
              >
                {removeFightMutation.isLoading ? (
                  <svg
                    className="w-4 h-4 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      className="opacity-25"
                    ></circle>
                    <path
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      className="opacity-75"
                    ></path>
                  </svg>
                ) : (
                  <XIcon className="w-4 h-4" aria-hidden="true" />
                )}
              </button>
            )}
          </div>

          {/* Fighters — consolidated moneyline + results per fighter */}
          <div
            className={`grid grid-cols-1 ${
              isMobile ? "" : "md:grid-cols-2"
            } gap-6`}
          >
            {fight.Fighters.map((fighter) => (
              <FighterDetails
                key={fighter.FighterId}
                fighter={fighter}
                moneyline={moneylines[fighter.FighterId]}
                onMoneylineChange={(e) =>
                  handleMoneylineChange(fighter.FighterId, e)
                }
                onUpdate={() =>
                  updateMoneyline(eventId, fight.FightId, fighter.FighterId)
                }
                isLoading={updatingMoneylineFor === fighter.FighterId}
                onUpdateOutcome={handleUpdateOutcome}
              />
            ))}
          </div>

          {/* Submit fight results for both fighters */}
          <div className="flex justify-center pt-2">
            <button
              onClick={handleSubmitAllOutcomes}
              disabled={updateFightResultsMutation.isLoading}
              className={`btn btn--primary px-6 py-2 ${
                updateFightResultsMutation.isLoading
                  ? "opacity-60 cursor-not-allowed"
                  : ""
              }`}
            >
              {updateFightResultsMutation.isLoading ? (
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      className="opacity-25"
                    ></circle>
                    <path
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      className="opacity-75"
                    ></path>
                  </svg>
                  Submitting...
                </div>
              ) : (
                "Submit Fight Results"
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      <RemoveFightConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {
          handleRemoveFight();
          setShowConfirmModal(false);
        }}
        fightName={fight.Fighters.map(
          (f) => `${f.FirstName} ${f.LastName}`,
        ).join(" vs. ")}
      />
    </div>
  );
};

export default FightCard;
